import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Copy, Check, Loader2, History } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { messageHistoryApi } from "@/lib/api";
import { getAuthHeaders } from "@/lib/auth";

interface HistoryEntry {
  id: string;
  message_content: string;
  message_type: string;
  config: Record<string, unknown>;
  created_at: string;
  generation_time_ms: number | null;
  project_name: string | null;
  project_id: number;
  sender: string | null;
  is_selected?: boolean;
  section1?: string;
  section2?: string;
  section3?: string;
  notes?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileUrl?: string;
  projectId?: number;
}

const HIDDEN_CONFIG_KEYS = new Set([
  "profile_data",
  "previous_messages",
  "calendly_link",
]);

function formatConfigValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "tak" : "nie";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function MessageHistoryModal({ open, onOpenChange, profileUrl, projectId }: Props) {
  const { t } = useTranslation("generator");
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !profileUrl || !projectId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({
          profile_url: profileUrl,
          project_id: String(projectId),
          limit: "50",
        });
        const response = await fetch(`${messageHistoryApi()}?${params.toString()}`, {
          method: "GET",
          headers: getAuthHeaders(),
          credentials: "include",
        });
        if (!response.ok) {
          console.error("Error fetching message history:", response.status);
          if (!cancelled) setEntries([]);
          return;
        }
        const data = await response.json();
        if (!cancelled) setEntries(Array.isArray(data?.messages) ? data.messages : []);
      } catch (err) {
        console.error("Error fetching history:", err);
        if (!cancelled) setEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, profileUrl, projectId]);

  const handleCopy = async (entry: HistoryEntry) => {
    try {
      await navigator.clipboard.writeText(entry.message_content || "");
      setCopiedId(entry.id);
      setTimeout(() => setCopiedId(null), 1500);
      toast({ title: "Skopiowano", description: "Treść wiadomości skopiowana." });
    } catch {
      toast({ title: "Błąd", description: "Nie udało się skopiować.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Historyczne ustawienia
          </DialogTitle>
          <DialogDescription>
            Poprzednie wygenerowane wiadomości dla tego profilu wraz z konfiguracją.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 -mr-1">
          {loading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Ładowanie historii…
            </div>
          )}

          {!loading && entries.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              Brak historii dla tego profilu i projektu.
            </div>
          )}

          {!loading && entries.length > 0 && (
            <Accordion type="multiple" className="space-y-2">
              {entries.map((entry, idx) => (
                <AccordionItem
                  key={entry.id}
                  value={entry.id}
                  className="border border-border rounded-lg px-3"
                >
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex flex-1 items-center justify-between gap-3 text-left">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          #{entries.length - idx} · {entry.message_type || "—"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.created_at).toLocaleString()}
                          {entry.sender ? ` · ${entry.sender}` : ""}
                        </span>
                      </div>
                      {entry.is_selected && (
                        <Badge variant="default" className="text-xs">
                          <Check className="h-3 w-3 mr-1" />
                          Wybrana
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-2">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                            Wiadomość
                          </h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(entry)}
                          >
                            {copiedId === entry.id ? (
                              <Check className="h-3 w-3 mr-1" />
                            ) : (
                              <Copy className="h-3 w-3 mr-1" />
                            )}
                            Kopiuj
                          </Button>
                        </div>
                        <div className="rounded-md bg-secondary/30 p-3 text-sm whitespace-pre-wrap">
                          {entry.message_content || "—"}
                        </div>
                      </div>

                      {(entry.section1 || entry.section2 || entry.section3) && (
                        <div className="space-y-2">
                          {entry.section1 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Sekcja 1
                              </h4>
                              <div className="rounded-md bg-secondary/20 p-2 text-sm whitespace-pre-wrap">
                                {entry.section1}
                              </div>
                            </div>
                          )}
                          {entry.section2 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Sekcja 2
                              </h4>
                              <div className="rounded-md bg-secondary/20 p-2 text-sm whitespace-pre-wrap">
                                {entry.section2}
                              </div>
                            </div>
                          )}
                          {entry.section3 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                Sekcja 3
                              </h4>
                              <div className="rounded-md bg-secondary/20 p-2 text-sm whitespace-pre-wrap">
                                {entry.section3}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {entry.notes && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                            Notatki
                          </h4>
                          <div className="rounded-md bg-secondary/20 p-2 text-sm whitespace-pre-wrap">
                            {entry.notes}
                          </div>
                        </div>
                      )}

                      <div>
                        <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                          Konfiguracja
                        </h4>
                        <div className="rounded-md border border-border divide-y divide-border text-xs">
                          <div className="flex justify-between gap-3 px-3 py-1.5">
                            <span className="text-muted-foreground">project</span>
                            <span className="font-mono text-right">
                              {entry.project_name || entry.project_id}
                            </span>
                          </div>
                          {entry.sender && (
                            <div className="flex justify-between gap-3 px-3 py-1.5">
                              <span className="text-muted-foreground">sender</span>
                              <span className="font-mono text-right">{entry.sender}</span>
                            </div>
                          )}
                          {Object.entries(entry.config || {})
                            .filter(([k]) => !HIDDEN_CONFIG_KEYS.has(k))
                            .map(([k, v]) => (
                              <div key={k} className="flex justify-between gap-3 px-3 py-1.5">
                                <span className="text-muted-foreground">{k}</span>
                                <span className="font-mono text-right break-all">
                                  {formatConfigValue(v)}
                                </span>
                              </div>
                            ))}
                          {entry.generation_time_ms != null && (
                            <div className="flex justify-between gap-3 px-3 py-1.5">
                              <span className="text-muted-foreground">generation_time_ms</span>
                              <span className="font-mono text-right">
                                {entry.generation_time_ms}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
